import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Checkbox, Col, Form, Input, InputNumber, message, Modal, Radio, Row, Select, Spin } from 'antd';
import React from 'react';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PaymentModeType, PaymentTermsType, PlanType, SystemAppType } from '@/constants';

import SoftwareComponentsInput from '@/components/FormItems/SoftwareComponentsInput';
import ServiceInput from '@/components/FormItems/ServiceInput';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: (reload?: boolean) => void;
  planId?: string;
};
export default function CreateUpdateModal({ planId, onClose }: Props) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (planId) {
      setLoading(true);
      apiSdk.PlanService.getEntityForUpdate({
        id: planId,
      })
        .then(res => {
          form.setFieldsValue({
            ...res,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [planId]);

  const onSubmit = () => {
    form
      .validateFields()
      .then(values => {
        setLoading(true);
        (planId
          ? apiSdk.PlanService.update({
              body: values,
            })
          : apiSdk.PlanService.create({
              body: values,
            })
        )
          .then(response => {
            if (response?.success) {
              onClose(true);
            } else {
              if (response.message) {
                message.error(t(response.message || ''));
              }
            }
          })
          .catch(error => {
            message.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {});
  };

  const availableApps = Form.useWatch('availableApps', form);
  return (
    <Modal
      style={{ top: 10 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form"
      title={!planId ? t('planPage_CreatePlan') : t('planPage_UpdatePlan')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={onSubmit}
    >
      <Spin spinning={loading}>
        <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 7, xl: 7 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('planPage_PlanName')} name="planName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('planPage_AvailableApps')} name="availableApps" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Checkbox.Group options={SystemAppType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('planPage_Components')} name="componentIds">
                <SoftwareComponentsInput mode="multiple" availableApps={availableApps} maxTagCount={undefined} />
              </Form.Item>
              <Form.Item label={t('planPage_NoOfRegister')} name="noOfRegister">
                <InputNumber min={0} className="w-100" />
              </Form.Item>

              <Form.Item label={t('planPage_MonthlyFee')} name="monthlyFee" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('planPage_TransactionLimit')} name="transactionLimit">
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('planPage_Turnover')} name="turnover">
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('planPage_TransactionFee')} name="transactionFee">
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('planPage_Services')} name="serviceIds">
                <ServiceInput mode="multiple" maxTagCount={undefined} />
              </Form.Item>
              <Form.Item label={t('planPage_PlanType')} name="planType">
                <Select options={PlanType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('planPage_IsPopular')} valuePropName="checked" name="isPopular">
                <Checkbox />
              </Form.Item>
              <Form.Item label={t('planPage_PaymentTerm')} name="paymentTermType">
                <Radio.Group options={PaymentTermsType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('planPage_PaymentMethodType')} name="paymentMethodType">
                <Select options={PaymentModeType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('base.Description')} name="description">
                <Input.TextArea />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
